// @flow
import {
    bulbTypes,
    deviceModels,
    devicesWithModelVariations,
    deviceTypes,
    plugTypes,
    regions
} from '~/core/constants/devices';
import type { DeviceModel, DeviceType, Region } from '~/core/types/devices';
import type { SenseSensorType, SensorLogEventType } from '~/core/types/sensor-logs';
import { senseSensorTypes, sensorLogTypes } from '~/core/constants/sensor-logs';

import type { DevicesIcon } from '~/core/constants/icons/devices-svg';
import devicesIcons from '~/core/constants/icons/devices-svg';
import generalIcons from '~/core/constants/icons/general-svg';

/*
// productTypeToIcon() has been moved to ../core/icons/svg-icons/map
// Left here for reference.
// Please remove/delete file when the other two functions (deviceTypeToIcon/sensorLogTypeToIcon) have been moved (if necessary)
export const productTypeToIcon = ({
    type,
    localeSuffix = 'UK',
    model
}: {
    type: DeviceType,
    localeSuffix?: Region,
    model?: DeviceModel
}): DevicesIcon => {
    // $FlowFixMe
    const iconMap: { [DeviceType]: DevicesIcon } = {
        [deviceTypes.COLOR_TUNEABLE_LIGHT]: devicesIcons.LIGHT,
        [deviceTypes.COLOR_LIGHT]: devicesIcons.LIGHT,
        [deviceTypes.CONTACT_SENSOR]: devicesIcons.DOOR_SENSOR,
        [deviceTypes.HEATING]: devicesIcons.HEATING,
        [deviceTypes.HIVE_BUTTON]: devicesIcons.HIVE_BUTTON,
        [deviceTypes.HIVE_CAMERA]: devicesIcons.CAMERA_DEHARO,
        [deviceTypes.HOT_WATER]: devicesIcons.HOT_WATER,
        [deviceTypes.LEAK_SENSOR]: devicesIcons.LEAK_SENSOR,
        [deviceTypes.MOTION_SENSOR]: devicesIcons.MOTION_SENSOR,
        [deviceTypes.NA_THERMOSTAT]: devicesIcons.FLAME_NA,
        [deviceTypes.PRODUCT_GROUP]: generalIcons.GROUP_CONTROL,
        [deviceTypes.SIGNAL_BOOSTER]: devicesIcons.SIGNAL_BOOST,
        [deviceTypes.SENSE]: devicesIcons.SENSE,
        [deviceTypes.TUNEABLE_LIGHT]: devicesIcons.LIGHT,
        [deviceTypes.WARM_WHITE_LIGHT]: devicesIcons.LIGHT
    };

    const regionSpecificIconMap: { [DeviceType]: { [Region]: DevicesIcon } } = {
        [deviceTypes.ACTIVE_PLUG]: {
            [regions.EU]: devicesIcons.PLUG_EU,
            [regions.IN]: devicesIcons.PLUG_IN,
            [regions.NA]: devicesIcons.PLUG_NA,
            [regions.UK]: devicesIcons.PLUG_UK
        },
        [deviceTypes.SMART_PLUG]: {
            [regions.EU]: devicesIcons.PLUG_EU,
            [regions.IN]: devicesIcons.PLUG_IN,
            [regions.NA]: devicesIcons.PLUG_NA,
            [regions.UK]: devicesIcons.PLUG_UK
        }
    };

    return (
        iconMap[type] ||
        // $FlowFixMe
        (typeAndModelIconMap[type] && typeAndModelIconMap[type][model]) ||
        (regionSpecificIconMap[type] && regionSpecificIconMap[type][localeSuffix]) ||
        devicesIcons.LINKED
    );
};*/

type typeAndModelIconMapType = {
    [DeviceType]: {
        [DeviceModel]: DevicesIcon
    }
};

const typeAndModelIconMap: typeAndModelIconMapType = {
    [deviceTypes.BOILER_MODULE]: {
        [deviceModels.SLR1]: devicesIcons.BOILER_MODULE_SLR1,
        [deviceModels.SLR2]: devicesIcons.BOILER_MODULE_SLR2
    },
    [deviceTypes.HUB]: {
        [deviceModels.NANO1]: devicesIcons.HUB_NANO1,
        [deviceModels.NANO2]: devicesIcons.HUB_NANO2,
        [deviceModels.SENSE]: devicesIcons.SENSE
    },
    [deviceTypes.THERMOSTAT]: {
        [deviceModels.SLT1]: devicesIcons.THERMOSTAT_SLT1,
        [deviceModels.SLT2]: devicesIcons.THERMOSTAT_SLT2,
        [deviceModels.SLT3]: devicesIcons.THERMOSTAT_SLT3,
        [deviceModels.SLT3b]: devicesIcons.THERMOSTAT_SLT3
    },
    [deviceTypes.THERMOSTAT_UI]: {
        [deviceModels.SLT1]: devicesIcons.THERMOSTAT_SLT1,
        [deviceModels.SLT2]: devicesIcons.THERMOSTAT_SLT2,
        [deviceModels.SLT3]: devicesIcons.THERMOSTAT_SLT3,
        [deviceModels.SLT3b]: devicesIcons.THERMOSTAT_SLT3
    },
    [deviceTypes.ACTIVE_PLUG]: {
        [deviceModels.SLP2d]: devicesIcons.PLUG_EU,
        [deviceModels.SLP2c]: devicesIcons.PLUG_NA,
        [deviceModels.SLP2b]: devicesIcons.PLUG_UK,
        [deviceModels.SLP2]: devicesIcons.PLUG_UK
    }
};

type regionToIconMapType = {
    [string]: DevicesIcon
};

const plugsAndRegionIconMap: regionToIconMapType = {
    [regions.EU]: devicesIcons.PLUG_EU,
    [regions.IN]: devicesIcons.PLUG_IN,
    [regions.NA]: devicesIcons.PLUG_NA,
    [regions.UK]: devicesIcons.PLUG_UK
};

export const deviceTypeToIcon = ({
    model,
    type,
    localeSuffix = regions.UK
}: {
    model?: DeviceModel,
    type: DeviceType,
    localeSuffix?: Region
}): DevicesIcon => {
    const defaultIcon = devicesIcons.LINKED;

    if (devicesWithModelVariations.includes(type) && model) {
        return (typeAndModelIconMap[type] && typeAndModelIconMap[type][model]) || devicesIcons.LINKED;
    }

    if (type === deviceTypes.NA_THERMOSTAT) {
        return devicesIcons.THERMOSTAT_SLT3;
    }

    if (bulbTypes.includes(type)) {
        return devicesIcons.LIGHT;
    }

    if (plugTypes.includes(type)) {
        return plugsAndRegionIconMap[localeSuffix] || defaultIcon;
    }

    if (type === deviceTypes.CONTACT_SENSOR) {
        return devicesIcons.DOOR_SENSOR;
    }

    if (type === deviceTypes.MOTION_SENSOR) {
        return devicesIcons.MOTION_SENSOR;
    }

    if (type === deviceTypes.SIGNAL_BOOSTER) {
        return devicesIcons.SIGNAL_BOOST;
    }

    if (type === deviceTypes.LEAK_SENSOR) {
        return devicesIcons.LEAK_SENSOR;
    }

    if (type === deviceTypes.HUE_BRIDGE) {
        return devicesIcons.PHILIPS_HUE_BRIDGE;
    }

    if (type === deviceTypes.HIVE_CAMERA) {
        return devicesIcons.CAMERA_DEHARO;
    }

    return defaultIcon;
};

export const sensorLogTypeToIcon = (type: SensorLogEventType, sensor?: SenseSensorType) => {
    const senseLogIconMap = new Map([
        [true, devicesIcons.LINKED],
        [sensor === senseSensorTypes.SMOKE_CO, generalIcons.SMOKE_CO],
        [sensor === senseSensorTypes.GLASS_BREAK, generalIcons.GLASS_BREAK],
        [sensor === senseSensorTypes.DOG_BARK, generalIcons.DOG_BARK]
    ]);

    const eventLogIconMap = new Map([
        [true, devicesIcons.LINKED],
        [type === sensorLogTypes.SOUND, senseLogIconMap.get(true)],
        [type === sensorLogTypes.UNMUTE, generalIcons.MONITORING_ON],
        [type === sensorLogTypes.MUTE, generalIcons.MONITORING_OFF],
        [type === sensorLogTypes.CLOSED || type === sensorLogTypes.OPENED, devicesIcons.DOOR_SENSOR],
        [type === sensorLogTypes.MOTION, devicesIcons.MOTION_SENSOR],
        [
            type === sensorLogTypes.FLOW_DETECTED ||
                type === sensorLogTypes.LEAK_DETECTED ||
                type === sensorLogTypes.LEAK_FIXED ||
                type === sensorLogTypes.USER_INTENDED_USAGE,
            devicesIcons.WATER_FLOWING
        ]
    ]);

    return eventLogIconMap.get(true);
};



// WEBPACK FOOTER //
// ./src/core/utils/svg-icons.js